/**
 * Button — legacy API over Astryx (see web/src/components/ui/Button.tsx).
 *
 * Real `<button>`s render Astryx's Button (themed by shared/theme/hireos.ts).
 * The `href`, `to`, and `link` forms keep the hand-rolled anchor/Link
 * rendering — Astryx has no link-shaped Button, and navigation isn't a
 * Button there.
 */
import { Button as AstryxButton } from "@astryxdesign/core/Button";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router";

import styles from "./Button.module.css";

interface ButtonBaseProps {
  variant?: "primary" | "secondary" | "outline" | "danger" | "link" | "ghost";
  size?: "sm" | "md" | "lg" | "icon";
  loading?: boolean;
  children: ReactNode;
  className?: string;
}

interface ButtonAsButton
  extends
    ButtonBaseProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> {
  href?: never;
  target?: never;
  rel?: never;
  to?: never;
}

/**
 * The anchor and Link forms carry only the looks Button.module.css draws:
 * Astryx's `outline`, `danger` and `ghost`, and its `lg` and `icon` sizes,
 * are real-button variants nothing navigates with.
 */
interface AnchorProps extends ButtonBaseProps {
  variant?: "primary" | "secondary" | "link";
  size?: "sm" | "md";
}

interface ButtonAsLink extends AnchorProps {
  href: string;
  target?: string;
  rel?: string;
  to?: never;
  onClick?: never;
  disabled?: never;
  type?: never;
}

interface ButtonAsRouterLink extends AnchorProps {
  /** react-router destination — renders an in-app `<Link>` (no page reload). */
  to: string;
  href?: never;
  target?: never;
  rel?: never;
  onClick?: never;
  disabled?: never;
  type?: never;
}

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsRouterLink;

const ASTRYX_VARIANT = {
  primary: "primary",
  secondary: "secondary",
  danger: "destructive",
  ghost: "ghost",
  outline: "outline", // custom variant defined in the hireos theme
} as const;

// The anchor and Link forms' own look — see Button.module.css. `variant="link"`
// is the one that is not a box: no padding, no border, no size.
function linkClasses(
  variant: NonNullable<ButtonBaseProps["variant"]>,
  size: NonNullable<ButtonBaseProps["size"]>,
  className: string,
) {
  if (variant === "link") return `${styles.link} ${className}`;
  return `${styles.anchor} ${styles[variant]} ${styles[size]} ${className}`;
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  className = "",
  children,
  ...props
}: ButtonProps) {
  if ("to" in props && props.to) {
    const { to } = props as ButtonAsRouterLink;
    return (
      <Link to={to} className={linkClasses(variant, size, className)}>
        {children}
      </Link>
    );
  }

  if ("href" in props && props.href) {
    const { href, target, rel, ...rest } = props as ButtonAsLink;
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        className={linkClasses(variant, size, className)}
        {...(rest as Record<string, unknown>)}
      >
        {children}
      </a>
    );
  }

  const {
    disabled,
    type,
    onClick,
    "aria-label": ariaLabel,
    ...buttonProps
  } = props as ButtonAsButton;

  if (variant === "link") {
    return (
      <button
        type={type ?? "button"}
        onClick={onClick}
        disabled={disabled || loading}
        className={`${styles.link} ${className}`}
        {...buttonProps}
      >
        {children}
      </button>
    );
  }

  const label = typeof children === "string" ? children : (ariaLabel ?? "");

  return (
    <AstryxButton
      label={label}
      variant={ASTRYX_VARIANT[variant] as "primary"}
      size={size === "icon" ? "sm" : size}
      isIconOnly={size === "icon"}
      icon={size === "icon" ? children : undefined}
      isLoading={loading}
      isDisabled={disabled}
      type={type}
      onClick={onClick}
      className={className}
      {...(buttonProps as Record<string, unknown>)}
    >
      {typeof children === "string" || size === "icon" ? undefined : (
        // Mixed children (an icon beside text) — see `.iconRow`.
        <span className={styles.iconRow}>{children}</span>
      )}
    </AstryxButton>
  );
}
